<template>
  <div class="new-card-setting__container mt-4">
    <div class="adding-card-form">
      <template v-if="isStripeCard">
        <div class="form-row">
          <div class="row-content">
            <div class="stripe-form__field">
              <div class="stripe-form__panel" :class="{ focus: focusType === 'cardNumber', invalid: !!cardNumberErrorMsg }">
                <div id="card-number" class="stripe__input" />
              </div>
              <p v-if="cardNumberErrorMsg" class="error-text">
                {{ cardNumberErrorMsg }}
              </p>
            </div>
          </div>
        </div>

        <div class="form-row">
          <div class="row-content mr-2">
            <div class="stripe-form__field">
              <div class="stripe-form__panel" :class="{ focus: focusType === 'cardExpiry', invalid: !!cardExpiryErrorMsg }">
                <div id="card-expiry" class="stripe__input" />
              </div>
              <p v-if="cardExpiryErrorMsg" class="error-text">{{ cardExpiryErrorMsg }}</p>
            </div>
          </div>
          <div class="row-content">
            <div class="stripe-form__field">
              <div class="stripe-form__panel d-flex justify-space-between" :class="{ focus: focusType === 'cardCvc', invalid: cardCVCError }">
                <div id="card-cvc" class="stripe__input" />

                <div class="question-icon__append" @click.stop="openCreditCardQuestion">
                  <funnow-icon max-width="20" :image-path="'question-icon__gray50.svg'" />
                </div>
              </div>

              <p v-if="cardCVCError" class="error-text">
                {{ $t('credit_card_error') }}
              </p>
            </div>
          </div>
        </div>
      </template>

      <div v-else class="form-row">
        <PaymentTappayCard
          ref="tapPayCardRef"
          :is-binding-card="props.isBindingCard"
          :is-payment-link="props.isPaymentLink"
          @update:card-verified="syncFormStatus"
        />
      </div>

      <CardholderVerificationForm
        v-if="isNeedCardholderVerification"
        v-model="cardholderModel"
        v-model:valid="isCardholderValid"
        :is-payment-link="props.isPaymentLink"
        @update:valid="syncFormStatus"
      />

      <p class="form-hint wd-text-gray80">
        {{ $t('add_credit_card_remind') }}
        <br />
        <span class="adding-card-form__hint">
          {{ $t('add_credit_card_remind_note') }}
        </span>
      </p>
      <p class="form-hint d-flex mb-0">
        <funnow-icon class="lock-icon mr-1" max-width="20" :image-path="'lock-icon.svg'" />
        {{ $t('add_card_guarantee') }}
      </p>
    </div>

    <credit-card-question-modal ref="creditCardQuestionModal" />
  </div>
</template>

<script setup lang="ts">
import type { Stripe, StripeCardCvcElement, StripeCardExpiryElement, StripeCardNumberElement } from '@stripe/stripe-js'
import { storeToRefs } from 'pinia'
// @ts-ignore
import Payment from 'payment'

import CreditCardQuestionModal from '@/components/dialog/payment/credit-card-question.vue'
import FunnowIcon from '@/components/icons/funnow-icon.vue'
import CardholderVerificationForm from '@/components/page/preview/CardholderVerificationForm.vue'
import PaymentTappayCard from '@/components/payment/PaymentTappayCard.vue'

import { useOrderPaymentStore } from '@/store-pinia/order-payment'
import { PaymentMethodProviderType, PaymentMethodsType, type TPCardResult } from '@/view-models/payment'

defineOptions({
  name: 'AddCreditCard',
})

interface Props {
  providerType?: number
  pubKey?: string
  isBindingCard?: boolean
  cardholder?: {
    name: string
    phone: string
    email: string
  }
  isPaymentLink?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  providerType: PaymentMethodProviderType.TapPay,
  pubKey: '',
  isBindingCard: false,
  cardholder: () => ({
    name: '',
    phone: '',
    email: '',
  }),
  isPaymentLink: false,
})

const emit = defineEmits<{
  'update:verifyErrorStatus': [value: boolean]
}>()

const { $store } = useNuxtApp()
const i18n = useI18n()
const route = useRoute()

let stripe: Stripe, stripeCardNumber: StripeCardNumberElement, stripeCardExpiry: StripeCardExpiryElement, stripeCardCVC: StripeCardCvcElement
const stripeStyle = {
  base: {
    padding: '0 12px',
    fontWeight: '400',
    fontSize: '15px',
    lineHeight: '39px',
    '::placeholder': { color: '#a7a7a9' },
  },
  invalid: {
    color: '#c03f45',
  },
}

// Store setup
const orderStore = useOrderPaymentStore()
const { orderDetail } = storeToRefs(orderStore)
const orderId = computed(() => orderDetail.value?.id)

// Refs
const creditCardQuestionModal = useTemplateRef('creditCardQuestionModal')
const tapPayCardRef = useTemplateRef('tapPayCardRef')

// Reactive data
const focusType = ref('')
const isStripeLoading = ref(false)
const isCardholderValid = ref(false)
const cardholderModel = defineModel<Props['cardholder']>('cardholder', {
  default: () => ({
    name: '',
    phone: '',
    email: '',
  }),
})

const cardNumberError = ref(false)
const cardExpiryError = ref(false)
const cardCVCError = ref(false)
const cardStatus = reactive({
  cardNumberCompleted: false,
  cardExpiryCompleted: false,
  cardCVCCompleted: false,
  cardNumberError,
  cardFundingError: false,
  cardExpiryError,
  cardExpiryDatesError: false,
  cardCVCError,
})

const { checkIsInvalidCard, checkCardExpired } = usePaymentCardInfo()

// Computed
const isStripeCard = computed(() => {
  return props.providerType === PaymentMethodProviderType.Stripe
})

const publishableKey = computed(() => {
  return (
    props.pubKey ||
    gatewayInfo.value?.publishable_key ||
    gatewayInfo?.value?.payment_methods?.find((method: any) => method.type === PaymentMethodsType.NewCreditCard)?.pub_key ||
    ''
  )
})

const isNeedCardholderVerification = computed(() => {
  return props.providerType === PaymentMethodProviderType.TapPay
})

const cardNumberErrorMsg = computed(() => {
  if (cardStatus.cardFundingError) {
    return i18n.t('credit_card_error_debit_card_not_accept')
  }
  if (cardStatus.cardNumberError) {
    return i18n.t('credit_card_error')
  }
  return ''
})

const cardExpiryErrorMsg = computed(() => {
  if (cardStatus.cardExpiryDatesError) {
    return i18n.t('credit_card_error_vaild_date_not_enough')
  }
  if (cardStatus.cardExpiryError) {
    return i18n.t('credit_card_error')
  }
  return ''
})

const gatewayInfo = computed(() => $store.getters['payment/gatewayInfo'])
const binCode = computed(() => $store.getters['payment/binCode'])

// Watchers
watch(isStripeCard, newVal => {
  if (newVal) {
    nextTick(() => {
      initStripeCard()
    })
  }
})

watch(publishableKey, newVal => {
  if (newVal) {
    nextTick(() => {
      initStripeCard()
    })
  }
})

// Lifecycle
onMounted(() => {
  if (isStripeCard.value) {
    initStripeCard()
  }
})

// Methods
async function createPaymentMethod() {
  const paymentResult = await stripe!.createPaymentMethod({
    type: 'card',
    card: stripeCardNumber,
  })
  return paymentResult
}

function focusInput(elementName: string) {
  focusType.value = elementName
}

function listenStripeEvent() {
  stripeCardCVC.on('ready', (event: any) => {
    isStripeLoading.value = false
  })

  stripeCardNumber.on('change', (event: any) => {
    cardStatus.cardNumberCompleted = event.complete ?? false
    cardStatus.cardNumberError = !!event.error
    if (event.complete) {
      stripeCardExpiry.focus()
    }
    verifyStripeNewCardForm()
  })
  stripeCardExpiry.on('change', (event: any) => {
    cardStatus.cardExpiryCompleted = event.complete ?? false
    cardStatus.cardExpiryError = !!event.error
    if (event.complete) {
      stripeCardCVC.focus()
    } else if (event.empty) {
      stripeCardNumber.focus()
    }
    verifyStripeNewCardForm()
  })
  stripeCardCVC.on('change', (event: any) => {
    cardStatus.cardCVCCompleted = event.complete ?? false
    cardStatus.cardCVCError = !!event.error
    if (event.empty) {
      stripeCardExpiry.focus()
    }
    verifyStripeNewCardForm()
  })

  const focusEvent = (event: any) => {
    focusInput(event.elementType)
  }
  stripeCardNumber.on('focus', focusEvent)
  stripeCardExpiry.on('focus', focusEvent)
  stripeCardCVC.on('focus', focusEvent)

  const blurEvent = (_event: any) => {
    focusType.value = ''
    verifyStripeNewCardForm()
  }
  stripeCardNumber.on('blur', blurEvent)
  stripeCardExpiry.on('blur', blurEvent)
  stripeCardCVC.on('blur', blurEvent)
}

function initStripeCard() {
  isStripeLoading.value = true
  stripe = window.Stripe!(publishableKey.value)
  const elements = stripe.elements()

  stripeCardNumber = elements.create('cardNumber', {
    style: stripeStyle,
    placeholder: i18n.t('credit_card_hint'),
  })
  stripeCardNumber.mount('#card-number')

  stripeCardExpiry = elements.create('cardExpiry', {
    style: stripeStyle,
    placeholder: 'MM / YY',
  })
  stripeCardExpiry.mount('#card-expiry')

  stripeCardCVC = elements.create('cardCvc', {
    style: stripeStyle,
    placeholder: 'CVC / CVV',
  })
  stripeCardCVC.mount('#card-cvc')
  listenStripeEvent()
}

function checkBinCode(cardNumber: string) {
  let code = 0
  if (!binCode.value) {
    return code
  }
  binCode.value.forEach((data: any) => {
    if (cardNumber.indexOf(data.code) === 0) {
      code = data.id
    }
  })
  return code
}

async function getPrime() {
  const primePayload = await tapPayCardRef.value?.getPrime()
  return primePayload || null
}

async function verifyStripeNewCardForm() {
  const isCardReady = cardStatus.cardNumberCompleted && cardStatus.cardExpiryCompleted && cardStatus.cardCVCCompleted
  if (!isCardReady) {
    syncFormStatus(false)
    return
  }

  if (props.isBindingCard) {
    try {
      const paymentResult = await createPaymentMethod()
      cardStatus.cardFundingError = checkIsInvalidCard(PaymentMethodProviderType.Stripe, paymentResult)
      cardStatus.cardExpiryDatesError = checkCardExpired({
        expirationMonth: paymentResult.paymentMethod?.card?.exp_month,
        expirationYear: paymentResult.paymentMethod?.card?.exp_year,
      })

      if (cardStatus.cardFundingError || cardStatus.cardExpiryDatesError) {
        syncFormStatus(false)
        return
      }
    } catch {
      syncFormStatus(false)
      return
    }
  }

  syncFormStatus(true)
}

function syncFormStatus(success: boolean | undefined) {
  const isSuccess = success ?? false
  const hasError = isNeedCardholderVerification.value ? !isCardholderValid.value || !isSuccess : !isSuccess

  emit('update:verifyErrorStatus', hasError)
}

function openCreditCardQuestion() {
  creditCardQuestionModal.value?.showModal()
}

// Expose methods for parent component access
defineExpose({
  createPaymentMethod,
  getPrime,
})
</script>

<style lang="scss">
.new-card-setting__container {
  .adding-card-form {
    .form-row {
      margin-bottom: 8px;
      display: flex;
      justify-content: space-between;
      align-items: flex-start;

      .row-content {
        width: 100%;

        .stripe-form__field {
          color: $red50;

          .stripe-form__panel {
            padding: 0 11px;
            height: 40px;
            border-style: solid;
            border-width: 1px;
            border-color: $gray40;
            border-radius: 4px;

            .stripe__input {
              width: 100%;
            }

            &.focus {
              border-color: $orange50;
            }

            &.invalid {
              border-color: $red50;
            }
          }

          .error-text {
            font-size: 13px;
            margin-top: 4px;
            margin-bottom: 4px;
          }
        }

        .question-icon__append {
          margin-top: 2px;
          display: flex;
          align-items: center;
          cursor: pointer;
        }
      }
    }

    .form-hint {
      font-size: 13px;
    }
  }
}
</style>
